import { conns } from '@cfg/axiosConfig.tsx';
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { AppointmentServiceLine } from '@erp/appointments/api/appointments.api';

export type ClinicalRecordStatus = 'draft' | 'scheduled' | 'attended' | 'no_show' | 'canceled';

export interface ClinicalRecord {
  id: number;
  customer_id: number | null;
  customer_name: string | null;
  customer_username: string | null;
  staff_user_id: number;
  staff_name: string | null;
  space_id: number;
  space_name: string | null;
  sale_id: number | null;
  status: ClinicalRecordStatus;
  start_at: string;
  end_at: string;
  notes: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  service_lines: AppointmentServiceLine[];
  history_entries_count: number;
  latest_history_note: string | null;
  latest_history_entry_id: number | null;
  latest_history_recorded_by: string | null;
}

export interface ClinicalRecordsListResponse {
  items: ClinicalRecord[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export type ClinicalHistoryStatusFilter = 'completed' | 'pending' | 'all';

export interface ClinicalRecordFilters {
  start?: string;
  end?: string;
  staff_user_id?: number;
  customer_id?: number;
  customer_search?: string;
  service_id?: number;
  service_search?: string;
  status?: ClinicalRecordStatus;
  history_status?: ClinicalHistoryStatusFilter;
  page?: number;
  page_size?: number;
}

export const clinicalRecordsQueryKey = ['erp', 'clinical-history'] as const;

/**
 * Objetivo: Normalizar los filtros recibidos desde la UI para la consulta de historias clínicas.
 * Entradas:
 *   - filters: Filtros opcionales enviados por el consumidor del hook
 * Salidas: Objeto con las claves y valores listos para ser utilizados por la API
 * Requerimientos: Convertir nombres de camelCase a snake_case para el backend
 */
const normalizeClinicalRecordFilters = (filters?: ClinicalRecordFilters) => {
  if (!filters) {
    return { page: 1, page_size: 50 };
  }

  return {
    start: filters.start,
    end: filters.end,
    staff_user_id:
      typeof filters.staff_user_id === 'number' &&
      Number.isFinite(filters.staff_user_id) &&
      filters.staff_user_id > 0
        ? Math.trunc(filters.staff_user_id)
        : undefined,
    customer_id:
      typeof filters.customer_id === 'number' &&
      Number.isFinite(filters.customer_id) &&
      filters.customer_id > 0
        ? Math.trunc(filters.customer_id)
        : undefined,
    customer_search: filters.customer_search?.trim() || undefined,
    status: filters.status,
    page: filters.page ?? 1,
    page_size: filters.page_size ?? 50,
  };
};

/**
 * Objetivo: Obtener desde el backend la lista de historias clínicas con filtros.
 * Entradas:
 *   - filters: Criterios opcionales para limitar la búsqueda de historias clínicas.
 * Salidas: Promesa que resuelve con la respuesta tipada `ClinicalRecordsListResponse`.
 * Requerimientos: Utilizar el conector centralizado `conns` y respetar la normalización de filtros.
 */
export const fetchClinicalRecords = async (
  filters?: ClinicalRecordFilters,
): Promise<ClinicalRecordsListResponse> => {
  const params = normalizeClinicalRecordFilters(filters);
  const { data } = await conns<ClinicalRecordsListResponse>('erp:appointments:clinical_records').get(
    params,
  );
  return data;
};

/**
 * Objetivo: Exponer un hook React Query para consumir historias clínicas con caching y revalidación.
 * Entradas:
 *   - filters: Filtros opcionales que modifican la clave del query para segmentar los datos.
 *   - options: Configuración adicional del query (staleTime, select, etc.).
 * Salidas: Resultado completo de `useQuery`, incluyendo estado, datos y helpers.
 * Requerimientos: Mantener una clave estable y actualizar automáticamente cuando cambien los filtros.
 */
export const useClinicalRecords = (
  filters?: ClinicalRecordFilters,
  options?: Partial<
    UseQueryOptions<ClinicalRecordsListResponse, Error, ClinicalRecordsListResponse>
  >,
): UseQueryResult<ClinicalRecordsListResponse, Error> => {
  return useQuery<ClinicalRecordsListResponse, Error, ClinicalRecordsListResponse>({
    queryKey: filters ? [...clinicalRecordsQueryKey, filters] : clinicalRecordsQueryKey,
    queryFn: () => fetchClinicalRecords(filters),
    staleTime: 30_000,
    ...options,
  });
};

/**
 * Hook de conveniencia para obtener los últimos N turnos de un cliente con historial clínico.
 * Pensado para usar dentro del modal de detalle de turno.
 */
export const useCustomerClinicalRecords = (
  customerId: number | null,
  options?: { pageSize?: number } & Partial<
    UseQueryOptions<ClinicalRecordsListResponse, Error, ClinicalRecordsListResponse>
  >,
): UseQueryResult<ClinicalRecordsListResponse, Error> => {
  const pageSize = options?.pageSize ?? 10;
  const filters: ClinicalRecordFilters = {
    customer_id: customerId ?? undefined,
    page_size: pageSize,
  };
  return useClinicalRecords(filters, {
    enabled: Boolean(customerId),
    staleTime: 60_000,
    ...options,
  });
};
